import {useIsMutating, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

/**
 * Use the useCustomMutation hook when performing a mutation in one component and needing to check its state in another component.
 * If both the mutation and state check occur within the same component, simply utilize the useMutation hook https://tanstack.com/query/v4/docs/react/reference/useMutation
 * In React Query version 5, the upcoming useMutationState() hook will be introduced and we can remove useCustomMutation.
 * See: https://github.com/TanStack/query/issues/2304
 * @param {string} mutationKey unique identifier to track and manage the state of the mutation
 * @param {function} mutationFn function that performs an asynchronous task and returns a promise.
 * @param {Object} options object having custom onSuccess and onError functions
 * @return {Object} mutation data and states
 */
export const useCustomMutation = (mutationKey: $TSFixMe, mutationFn: $TSFixMe, options?: $TSFixMe) => {
  const queryClient = useQueryClient();
  const query = useQuery(['CustomMutation', mutationKey]);
  const queryError = useQuery(['CustomMutationError', mutationKey]);
  const mutation = useMutation(
    async(...params: $TSFixMe) => {
      queryClient.setQueryData(['CustomMutationError', mutationKey], false);
      return await mutationFn(...params);
    },
    {
      mutationKey: [mutationKey],
      onSuccess: (data) => {
        queryClient.setQueryData(['CustomMutation', mutationKey], data);
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      },
      onError: (err) => {
        queryClient.setQueryData(['CustomMutationError', mutationKey], err);
        if (options?.onError) {
          options.onError(err);
        }
      },
    }
  );
  const isLoading = useIsMutating(mutationKey);
  return {
    ...mutation,
    data: query.data,
    error: queryError.data,
    isLoading: !!isLoading,
    isSuccess: !!query.data,
    isError: !!queryError.data,
  };
};
